/*
 * This browser's push token: saving it once, and releasing it at sign-out.
 *
 * Ported from the main site (mintly-ui src/lib/push/releasePushToken.ts) so the two behave the
 * same way. Kept free of imports so it can be tested with plain fakes; usePushNotifications and
 * signOutPush wire the real Firebase and GraphQL calls.
 *
 * A web push token belongs to the browser, not to the person. The backend stores it against
 * whoever saved it last, so:
 *   - it is saved when it is new, has rotated, or was last saved for somebody else (a shared
 *     machine), and otherwise not at all -- not once per page load;
 *   - sign-out deletes it on the backend BEFORE the session goes (the mutation needs the session),
 *     or the next person at this browser would keep receiving the previous person's alerts.
 */

export const FCM_TOKEN_STORAGE_KEY = 'cc_fcm_token';
/** The staff user id the stored token was last saved under, so a different user re-saves it. */
export const FCM_TOKEN_OWNER_STORAGE_KEY = 'cc_fcm_token_owner';

export const PUSH_RELEASE_TIMEOUT_MS = 2000;

type TokenStorage = Pick<Storage, 'getItem' | 'removeItem'>;
type WritableTokenStorage = TokenStorage & Pick<Storage, 'setItem'>;

function safeGet(storage: TokenStorage | null | undefined, key: string) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeRemove(storage: TokenStorage | null | undefined, key: string) {
  try {
    storage?.removeItem(key);
  } catch {
    // Storage blocked: nothing to forget.
  }
}

export function needsPushTokenSave(
  token: string,
  userId: string,
  storage: TokenStorage | null | undefined,
): boolean {
  return (
    safeGet(storage, FCM_TOKEN_STORAGE_KEY) !== token ||
    safeGet(storage, FCM_TOKEN_OWNER_STORAGE_KEY) !== userId
  );
}

/**
 * Save `token` for `userId` unless this browser already did. Records it only after the save
 * succeeded, so a failed save is retried on the next load rather than believed. Returns whether a
 * save was sent.
 */
export async function savePushTokenOnce({
  token,
  userId,
  storage,
  save,
}: {
  token: string;
  userId: string;
  storage: WritableTokenStorage | null | undefined;
  save: (token: string) => Promise<unknown>;
}): Promise<boolean> {
  if (!needsPushTokenSave(token, userId, storage)) return false;
  await save(token);
  try {
    storage?.setItem(FCM_TOKEN_STORAGE_KEY, token);
    storage?.setItem(FCM_TOKEN_OWNER_STORAGE_KEY, userId);
  } catch {
    // Storage refused: the next load saves again, which the backend upserts harmlessly.
  }
  return true;
}

export type ReleasePushTokenDeps = {
  storage: TokenStorage | null | undefined;
  /** Removes the token from the backend. Needs the session, so it runs first. */
  deleteRemote: (token: string) => Promise<unknown>;
  /** Revokes the token at Firebase and drops the browser subscription. */
  deleteLocal?: () => Promise<unknown>;
  timeoutMs?: number;
  log?: (message: string, detail?: unknown) => void;
};

const TIMED_OUT = Symbol('timed-out');

function within<T>(promise: Promise<T>, ms: number): Promise<T | typeof TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/**
 * Resolves once the token is released or the time is up. Never rejects and never takes longer
 * than `timeoutMs`: a push problem must not stop anyone signing out.
 */
export async function releasePushToken({
  storage,
  deleteRemote,
  deleteLocal,
  timeoutMs = PUSH_RELEASE_TIMEOUT_MS,
  log = () => {},
}: ReleasePushTokenDeps): Promise<void> {
  const token = safeGet(storage, FCM_TOKEN_STORAGE_KEY);

  const work = (async () => {
    if (token) {
      try {
        await deleteRemote(token);
      } catch (err) {
        log('[push] backend token delete failed', err);
      }
    }
    if (token && deleteLocal) {
      try {
        await deleteLocal();
      } catch (err) {
        log('[push] firebase token delete failed', err);
      }
    }
  })();

  try {
    const outcome = await within(work, timeoutMs);
    if (outcome === TIMED_OUT) log('[push] token release timed out');
  } catch (err) {
    log('[push] token release failed', err);
  } finally {
    safeRemove(storage, FCM_TOKEN_STORAGE_KEY);
    safeRemove(storage, FCM_TOKEN_OWNER_STORAGE_KEY);
  }
}
